import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
} from '@mui/material';
import { toastError, toastSuccess } from '@onestep/coreui/Toast';
import classNames from 'classnames';
import { StatusCodes } from 'http-status-codes';
import { pickBy } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import { ReactComponent as CalenderIcon } from '../../../../assets/icons/mobileCalendar.svg';
import CheckboxField from '../../../../components/Inputs/CheckboxField';
import Select from '../../../../components/Inputs/Select';
import TextField from '../../../../components/Inputs/TextField';
import { trackTiming } from '../../../../config/mixpanel';
import useQueryParam from '../../../../hooks/useCustomQueryParam';
import useIsPrimaryTheme from '../../../../hooks/useIsPrimaryTheme';
import useLoggedUser from '../../../../hooks/useLoggedUser';
import useMixpanel from '../../../../hooks/useMixpanel';
import useTimezoneOptions from '../../../../hooks/useTimezoneOptions';
import { usePatientProfileService } from '../../../../services/patientProfileService/index';
import {
  formatTime,
  getDateWithLocalOffsetRemoved,
  getDuration,
  getTimeZone,
  substract,
} from '../../../../services/timeService/index';
import { dateTimeFormat, unitOfTime } from '../../../../services/timeService/types';
import { eventModalContext } from '../../Timeline';
import eventSelectFields, {
  assistiveLevelSelectInput,
  eventContactOptions,
  eventType,
  responseSelectInput,
} from '../events-fields-options';
import useRtmEventsService from '../service';
import DecisionText from '../Text/DecisionText';
import EventTimeWarning from '../Text/EventTimeWarning';
import type { IClinicalVersion } from '../types';
import CloseDrawer from './CloseDrawer';
import confirmAmend from './confirm-amend';
import styles from './index.module.scss';
import { useClinicalEventsModal } from './Provider';
import rtmConfirmAlert from './RTMConfirmAlert';

const trackActivityForm = 'clicked: add_activity_event';
export const commentMonitorInitialText =
  'Clinician assessment of the data gathered from the OneStep device completed. The skilled clinical analysis of data revealed:';
export const commentMonitorConditionsText = `The patient/CG acknowledges understanding assessment of the skilled provider and agrees to a modification of the plan`;
export const educationToInitialText = `resulting in Clinician's decision to provide education to:`;

export interface IPatientEventData extends IClinicalVersion {
  serviceTime: string;
  contactOther?: string;
  durationInMinutes?: number;
  agreeToPlan?: boolean;
  educationTo?: string;
  timezone?: string;
}

const ClinicalEventForm = ({
  onChange,
  newItem,
}: {
  onChange?: (uuid: string) => void;
  newItem?: boolean;
}) => {
  const { patientUuid = '' } = useParams<{ patientUuid: string }>();
  const { myProfile } = useLoggedUser();
  const { setEventUuid } = useContext(eventModalContext);
  const {
    contextData: { clinicalEventUuid },
    contextData,
    setContextData,
  } = useClinicalEventsModal();
  const { data, getEvent, insertAction, ammendAction } = useRtmEventsService(patientUuid);
  const [newEvent, setNewEvent] = useQueryParam<string | undefined>('newEventUuid');
  const { episodeOfCareInfo } = usePatientProfileService();
  const isNewItem = newItem || contextData.isNewItem;
  const serviceTimeRef = useRef<HTMLInputElement | null>(null);
  const isPrimaryTheme = useIsPrimaryTheme();

  const { track } = useMixpanel({
    patientUuid,
  });
  const clinicalEvent = clinicalEventUuid ? getEvent(clinicalEventUuid) : null;

  const localeTimezone = getTimeZone();

  const timezoneOptions = useTimezoneOptions(true);

  const currentVersion = clinicalEvent?.versions?.[0];
  const defaultValues = clinicalEvent
    ? {
        ...currentVersion,
        serviceTime: formatTime(
          clinicalEvent.serviceTime,
          dateTimeFormat.SERVER_DATE_TIME_FORMAT,
          clinicalEvent.localeTimezone || localeTimezone,
        ),
        durationInMinutes: getDuration(
          Number(currentVersion?.durationInSeconds),
          unitOfTime.seconds,
        ).asMinutes(),
        ...(currentVersion?.contact && // add default values for other inputs
          !eventContactOptions?.find((option) => option.value === currentVersion?.contact) && {
            contact: 'other',
            contactOther: currentVersion?.contact,
          }),
        ...(currentVersion?.eventName === eventType.monitoringAndAssessment && {
          comment: currentVersion?.comment
            ?.replace(commentMonitorInitialText, '')
            ?.split(educationToInitialText)?.[0],
          educationTo: currentVersion?.comment
            ?.split(educationToInitialText)?.[1]
            ?.replace(commentMonitorConditionsText, ''),
          agreeToPlan: true,
        }),
      }
    : ({
        eventName: newEvent ?? '',
      } as IPatientEventData);

  useEffect(() => {
    trackTiming(trackActivityForm);
  }, []);

  const {
    formState: { isSubmitting, isValid, isDirty },
    control,
    watch,
    handleSubmit,
    register,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues,
  });
  const type = watch('eventName') ?? eventType.educationOrTraining;
  const contactWatch = watch('contact') === 'other';
  const { ref, ...rest } = register('serviceTime', { required: true });

  const onConfirmSubmit = async (form: IPatientEventData) => {
    let insertedEventUuid = '';
    form.userUuid = myProfile?.uuid ?? '';
    form.serviceTime = formatTime(
      form.serviceTime,
      dateTimeFormat.SERVER_DATE_TIME_WITH_S_FORMAT,
      form.timezone || localeTimezone,
    );

    if (form.durationInMinutes) {
      const durationTime = Number(form?.durationInMinutes);
      form.durationInSeconds = getDuration(durationTime, unitOfTime.minutes).asSeconds();
      delete form.durationInMinutes;
    }
    if (form.contact === 'other') {
      form.contact = form.contactOther ?? '';
      delete form.contactOther; // no need to send this input in the api
    }

    if (form.agreeToPlan) {
      form.comment =
        commentMonitorInitialText +
        ' ' +
        form.comment +
        ' ' +
        educationToInitialText +
        ' ' +
        form.educationTo +
        ' ' +
        commentMonitorConditionsText;
      delete form.agreeToPlan;
    }
    try {
      if (isNewItem) {
        insertedEventUuid = await insertAction({
          serviceTime: getDateWithLocalOffsetRemoved(
            form.serviceTime,
            dateTimeFormat.SERVER_DATE_TIME_FORMAT,
          ),
          localeTimezone: form.timezone || localeTimezone,
        });
        delete form.timezone;
        track(trackActivityForm, {
          component: 'reported_events',
          action_type: 'add_activity_event',
        });
        onChange?.(insertedEventUuid); // highlight new added event
      }
      form.uuid = (insertedEventUuid || clinicalEventUuid) ?? '';
      await ammendAction({
        data: pickBy(form) as IClinicalVersion,
        isMonitoring: form.eventName == eventType.monitoringAndAssessment,
      });
      toastSuccess({ content: `Event ${isNewItem ? 'created' : 'amended'} successfully!` });
      rtmConfirmAlert({
        eventTime: form.serviceTime,
        type,
        data,
        episodeofCareDate: episodeOfCareInfo?.data,
        toggleBillingPeriod: (data) => episodeOfCareInfo.toggleBillingPeriod(data),
        isNewItem,
      });
    } catch (error) {
      toastError({
        content: `Failed to ${isNewItem ? 'create' : 'amend'} event! ${
          error?.response.status === StatusCodes.CONFLICT ? error?.response?.data?.message : ''
        }`,
      });
    } finally {
      setContextData();
      setEventUuid();
      setNewEvent(undefined);
    }
  };

  const onSubmit = async (form: IPatientEventData) => {
    if (!isNewItem) {
      confirmAmend({ onClick: async () => await onConfirmSubmit(form) });
      // do confirm
    } else {
      await onConfirmSubmit(form);
    }
  };

  return (
    <>
      <CloseDrawer className={styles.close} isDirty={isDirty}>
        <CloseIcon />
      </CloseDrawer>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={classNames(styles.form, styles.eventsForm)}
      >
        <div className={'flex-grow'}>
          {eventSelectFields?.map(({ options, name, label, required }) => {
            return (
              <React.Fragment key={name}>
                <FormControl key={name} classes={{ root: 'w-full' }}>
                  <InputLabel shrink>{label}</InputLabel>
                  <Controller
                    name={name}
                    rules={{
                      required: required ?? false,
                    }}
                    defaultValue={''}
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={options}
                        className={styles.select}
                        MenuProps={{
                          classes: {
                            paper: classNames(styles.selectPaper, {
                              [styles.primaryP3Main]: isPrimaryTheme,
                            }),
                          },
                        }}
                        variant="outlined"
                        notched
                        displayEmpty={true}
                        renderValue={(value) => (
                          <div
                            className={classNames({
                              [styles.placeholder]: !value,
                            })}
                          >
                            {value
                              ? options?.find((option) => option?.value === value)?.label
                              : label}
                          </div>
                        )}
                        {...field}
                      />
                    )}
                  />
                </FormControl>
                {name === 'contact' && contactWatch ? (
                  <div className={classNames(styles.InputField)}>
                    <TextField
                      {...register('contactOther', { required: true })}
                      label={'Contact Other'}
                      placeholder={'Contact Other'}
                      variant="outlined"
                      fullWidth
                      inputClassName={styles.inputField}
                    />
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
          <div className={'flex gap-x-2 flex-wrap'}>
            <TextField
              type="datetime-local"
              {...(isNewItem && {
                defaultValue: formatTime('', dateTimeFormat.SERVER_DATE_TIME_FORMAT),
              })}
              required
              {...rest}
              name="serviceTime"
              ref={(e: HTMLInputElement) => {
                ref(e);
                serviceTimeRef.current = e;
              }}
              disabled={!isNewItem}
              inputProps={{
                min: formatTime(
                  substract(unitOfTime.day, 30),
                  dateTimeFormat.SERVER_DATE_TIME_FORMAT,
                ),
                max: formatTime('', dateTimeFormat.SERVER_DATE_TIME_FORMAT),
              }}
              wrapperClassName="flex-grow"
              inputClassName={classNames(styles.inputField, styles.timeField)}
              label={'Event time'}
              placeholder={'Event time'}
              variant="outlined"
              InputProps={{
                endAdornment: isMobile ? (
                  <InputAdornment position="end" className={styles.timeIcon}>
                    <div onClick={() => serviceTimeRef?.current?.showPicker()}>
                      <CalenderIcon />
                    </div>
                  </InputAdornment>
                ) : null,
              }}
            />
            <FormControl classes={{ root: styles.timezone }}>
              <InputLabel shrink>Event timezone</InputLabel>
              <Controller
                render={({ field }) => (
                  <Select
                    options={timezoneOptions}
                    className={styles.select}
                    MenuProps={{
                      classes: {
                        paper: classNames(styles.selectPaper, {
                          [styles.primaryP3Main]: isPrimaryTheme,
                        }),
                      },
                    }}
                    disabled={!isNewItem}
                    notched
                    variant="outlined"
                    displayEmpty={true}
                    renderValue={(value) => (
                      <div
                        className={classNames({
                          [styles.placeholder]: !value,
                        })}
                      >
                        {value
                          ? timezoneOptions?.find((option) => option?.value === value)?.label
                          : 'Event timezone'}
                      </div>
                    )}
                    {...field}
                  />
                )}
                name={'timezone'}
                defaultValue={clinicalEvent?.localeTimezone ?? localeTimezone}
                rules={{
                  required: true,
                }}
                control={control}
              />
            </FormControl>
            {!isNewItem ? (
              <div className={classNames('w-full mt-0.5', styles.warningLabel)}>
                <EventTimeWarning />
              </div>
            ) : null}
          </div>
          <FormControl classes={{ root: 'w-full' }}>
            <InputLabel shrink>{assistiveLevelSelectInput.label}</InputLabel>
            <Controller
              name={assistiveLevelSelectInput.name}
              rules={{
                required: assistiveLevelSelectInput.required ?? false,
              }}
              defaultValue={''}
              control={control}
              render={({ field }) => (
                <Select
                  options={assistiveLevelSelectInput.options}
                  className={styles.select}
                  MenuProps={{
                    classes: {
                      paper: classNames(styles.selectPaper, {
                        [styles.primaryP3Main]: isPrimaryTheme,
                      }),
                    },
                  }}
                  variant="outlined"
                  notched
                  displayEmpty={true}
                  renderValue={(value) => (
                    <div
                      className={classNames({
                        [styles.placeholder]: !value,
                      })}
                    >
                      {value
                        ? assistiveLevelSelectInput.options?.find(
                            (option) => option?.value === value,
                          )?.label
                        : assistiveLevelSelectInput.label}
                    </div>
                  )}
                  {...field}
                />
              )}
            />
          </FormControl>
          <FormControl classes={{ root: 'w-full' }}>
            <InputLabel shrink>{responseSelectInput.label}</InputLabel>
            <Controller
              name={responseSelectInput.name}
              rules={{
                required: responseSelectInput.required ?? false,
              }}
              defaultValue={''}
              control={control}
              render={({ field }) => (
                <Select
                  options={responseSelectInput.options}
                  className={styles.select}
                  MenuProps={{
                    classes: {
                      paper: classNames(styles.selectPaper, {
                        [styles.primaryP3Main]: isPrimaryTheme,
                      }),
                    },
                  }}
                  variant="outlined"
                  notched
                  displayEmpty={true}
                  renderValue={(value) => (
                    <div
                      className={classNames({
                        [styles.placeholder]: !value,
                      })}
                    >
                      {value
                        ? responseSelectInput.options?.find((option) => option?.value === value)
                            ?.label
                        : responseSelectInput.label}
                    </div>
                  )}
                  {...field}
                />
              )}
            />
          </FormControl>
          <TextField
            type="number"
            {...register('durationInMinutes', {
              required: true,
              min: 1,
            })}
            fullWidth
            wrapperClassName="mb-2"
            label={'Duration (minutes)'}
            inputClassName={styles.inputField}
            variant="outlined"
            placeholder={'Duration (minutes)'}
          />

          {type == eventType.monitoringAndAssessment ? <DecisionText /> : null}
          <div
            className={
              type === eventType.monitoringAndAssessment ? classNames(styles.InputField) : ''
            }
          >
            {type == eventType.monitoringAndAssessment ? (
              <div className={classNames('text-sm pb-4', styles.infoLabel)}>
                {commentMonitorInitialText}
              </div>
            ) : null}

            <TextField
              placeholder={
                type == eventType.educationOrTraining
                  ? 'Please describe here the onboarding scenario...'
                  : 'Please describe here...'
              }
              multiline
              rows={4}
              fullWidth
              {...register('comment', { required: true })}
              label={'Description'}
              variant="outlined"
              inputClassName={styles.inputField}
            />
          </div>
          {type === eventType.monitoringAndAssessment ? (
            <div className={classNames(styles.InputField)}>
              <div
                className={classNames('text-sm pb-4', styles.firstLetter, styles.infoLabel)}
              >
                {educationToInitialText}
              </div>

              <TextField
                placeholder={'Please describe here...'}
                multiline
                rows={4}
                fullWidth
                {...register('educationTo', { required: true })}
                label={'Monitoring Details'}
                variant="outlined"
                inputClassName={styles.inputField}
              />
            </div>
          ) : null}
          {contactWatch && type === eventType.educationOrTraining ? (
            <div className="seprated"></div>
          ) : null}

          {type === eventType.monitoringAndAssessment ? (
            <FormControl>
              <Controller
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <CheckboxField
                        {...field}
                        classes={{ root: styles.checkbox }}
                        defaultChecked={defaultValues?.agreeToPlan ?? false}
                      />
                    }
                    label={commentMonitorConditionsText}
                    className={styles.checkboxLabel}
                  />
                )}
                name="agreeToPlan"
                defaultValue={defaultValues?.agreeToPlan ?? false}
                control={control}
                rules={{
                  required: true,
                }}
              />
            </FormControl>
          ) : null}
        </div>
        <div
          className={classNames(
            'flex gap-2 items-center justify-end',
            styles.actions,
          )}
        >
          <CloseDrawer
            className={'font-semibold text-sm text-neutrals-n-400'}
            isDirty={isDirty}
          >
            Cancel
          </CloseDrawer>
          <Button
            variant={'contained'}
            color="primary"
            type="submit"
            disabled={!isValid || isSubmitting}
            classes={{ root: styles.addBtn }}
            className="font-semibold px-8"
          >
            {isSubmitting ? (
              <CircularProgress size={15} color="inherit" />
            ) : isNewItem ? (
              'Create event'
            ) : (
              'Amend event'
            )}
          </Button>
        </div>
      </form>
    </>
  );
};

export default observer(ClinicalEventForm);
